import { filterClasses } from "@swivl-mono/helpers";
import { BotSequenceSuppressedEventStats, BotSequenceSuppressedOutcome } from "@swivl-mono/types";

import { OutcomeChip } from "../SequenceSuppressions/SequenceSuppressions.Shared";

interface ByReasonCardProps {
  stats: BotSequenceSuppressedEventStats | null;
  activeOutcome: BotSequenceSuppressedOutcome | null;
  activeReason: string | null;
  onDrill: (outcome: BotSequenceSuppressedOutcome | null, reason: string | null) => void;
}

/**
 * The not-sent reason breakdown (the former Suppressions page's "By reason" table, absorbed
 * into Sequences — ENG-3811). Shown while the Not sent tile is selected (Chuck, 2026-10-22):
 * reasons only exist for not-sent rows. A row click drills the feed to that reason; clicking
 * the active row clears it.
 */
export function ByReasonCard({ stats, activeOutcome, activeReason, onDrill }: ByReasonCardProps): React.ReactElement {
  const reasons = stats?.reasons ?? [];
  const total = stats?.tiles.total ?? 0;
  return (
    <section className="rounded-lg border border-grey-200 bg-white p-4">
      <h2 className="mb-2 text-sm font-semibold">By reason</h2>
      {reasons.length === 0 ? (
        <p className="text-sm text-grey-500">No reasons recorded for these filters.</p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead className="text-grey-500">
            <tr>
              <th className="py-1">Reason</th>
              <th>Outcome</th>
              <th className="text-right">Count</th>
              <th className="text-right">% of not sent</th>
            </tr>
          </thead>
          <tbody>
            {reasons.map((row) => {
              const isActive = activeOutcome === row.outcome && activeReason === row.reason;
              const share = total > 0 ? Math.round((row.count / total) * 100) : 0;
              return (
                <tr
                  key={`${row.outcome}-${row.reason}`}
                  onClick={() => onDrill(isActive ? null : row.outcome, isActive ? null : row.reason)}
                  className={filterClasses("cursor-pointer border-t border-grey-100 hover:bg-grey-50", isActive && "bg-orange-50")}
                >
                  <td className="py-1 font-medium">{row.reason}</td>
                  <td>
                    <OutcomeChip outcome={row.outcome} />
                  </td>
                  <td className="text-right">{row.count}</td>
                  <td className="text-right text-grey-500">{share}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
